import React, { useState } from 'react';
import classnames from '../../core/utils/classnames.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Button from '../../core/components/Button.tsx';
import { useTranslation } from 'react-i18next';
import { createPasskeyCredentials, isWebauthnSupported } from '../utils.ts';
import { useMutation, useQuery } from '@apollo/client';
import { GET_MFA_DEVICE_SETUP_DATA, GET_MFA_DEVICES, REGISTER_MFA_DEVICE, UNREGISTER_MFA_DEVICE } from '../queries.ts';
import Modal from '../../core/components/Modal.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import IntegerField from '../../core/components/Fields/IntegerField.tsx';
import { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import { faFingerprint, faKey, faMobile, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { useToaster } from '../../core/components/Toast.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import FormField from '../../core/components/FormField.tsx';

const PasskeyIcon = withIcon(faFingerprint);
const AuthenticatorAppIcon = withIcon(faMobile);
const RecoveryCodeIcon = withIcon(faKey);
const DeleteIcon = withIcon(faTrashCan);
const SuccessIcon = withIcon(faCircleCheck);

const TwoFactorMethod = ({
    className,
    isActive,
    icon,
    title,
    description,
    button,
    children,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    isActive?: boolean;
    icon: React.ReactNode;
    title: React.ReactNode;
    description: React.ReactNode;
    button: React.ReactNode;
}) => {
    const hasChildren = React.Children.count(children) > 0;
    return (
        <div className="flex flex-col">
            <div
                className={classnames(
                    'flex flex-col items-start gap-4 py-4 px-5 rounded bg-surface-weak border border-line-weak md:flex-row md:items-center',
                    hasChildren && 'rounded-b-none',
                    className
                )}
                {...props}
            >
                <div className="text-2xl flex-none flex items-center justify-center w-6 text-content-strong">
                    {icon}
                </div>
                <div className="flex flex-col flex-1">
                    <span className="text-content-strong font-medium">{title}</span>
                    <span className="text-sm text-content-medium">{description}</span>
                </div>

                <div className="flex-none">{button}</div>
            </div>

            {hasChildren && (
                <div className="flex flex-col rounded-b bg-surface-medium border border-line-weak border-t-0">
                    {children}
                </div>
            )}
        </div>
    );
};

const TwoFactorMethodDevice = ({
    className,
    name,
    description,
    button,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    name: React.ReactNode;
    description: React.ReactNode;
    button: React.ReactNode;
}) => {
    return (
        <div
            className={classnames(
                'flex flex-col items-start gap-2 border-t border-line-weak first:border-t-0 py-4 px-5 md:flex-row md:items-center md:gap-0',
                className
            )}
            {...props}
        >
            <div className="flex flex-col gap-0.5 flex-1 md:pl-10">
                <span className="text-content-strong text-sm font-medium">{name}</span>
                <span className="text-sm text-content-medium">{description}</span>
            </div>

            <div className="flex-none">{button}</div>
        </div>
    );
};

const AddRecoveryCodesModalButton = ({
    onSubmit,
    ...props
}: React.ComponentPropsWithRef<typeof Button> & {
    onSubmit: ({ deviceType, name, payload }: { deviceType: string; name: string; payload: any }) => Promise<void>;
}) => {
    const { t } = useTranslation('users');

    const [isOpen, setIsOpen] = useState(false);

    const [name, setName] = useState('');

    const handleSubmit = async () => {
        try {
            await onSubmit({
                deviceType: 'static',
                name: name || t('mfa.static.defaultDeviceName'),
                payload: { tokens },
            });
            setIsOpen(false);
        } catch (e) {}
    };

    const { data } = useQuery(GET_MFA_DEVICE_SETUP_DATA, { skip: !isOpen, variables: { deviceType: 'static' } });

    const tokens = data?.mfaDeviceSetupData?.tokens;

    return (
        <>
            <Button onClick={() => setIsOpen(true)} {...props} disabled={!isWebauthnSupported || props.disabled}>
                {t('mfa.addButton')}
            </Button>

            {isOpen && (
                <Modal
                    onClose={() => setIsOpen(false)}
                    title={t('mfa.static.addModalTitle')}
                    buttons={
                        <>
                            <Button onClick={() => setIsOpen(false)}>{t('mfa.cancelButton')}</Button>
                            <Button variant="primary" onClick={handleSubmit}>
                                {t('mfa.saveButton')}
                            </Button>
                        </>
                    }
                >
                    <div className="w-[28rem] max-w-full flex flex-col gap-6">
                        <p className="text-content-strong text-base">{t('mfa.static.addModalDescription')}</p>

                        <div className="grid grid-cols-3 gap-2 my-2 items-center justify-center">
                            {tokens?.map((token) => (
                                <div className="flex justify-center items-center">
                                    <pre
                                        key={token}
                                        className="inline-block text-lg rounded bg-surface-strong px-2.5 py-1 text-content-strong font-medium"
                                    >
                                        {token}
                                    </pre>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-content-strong text-base font-medium">{t('mfa.nameLabel')}</label>
                            <StringField placeholder={t('mfa.namePlaceholder')} value={name} onValueChange={setName} />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

const AddPasskeyModalButton = ({
    onSubmit,
    ...props
}: React.ComponentPropsWithRef<typeof Button> & {
    onSubmit: ({ deviceType, name, payload }: { deviceType: string; name: string; payload: any }) => Promise<void>;
}) => {
    const { t } = useTranslation('users');

    const [isOpen, setIsOpen] = useState(false);

    const [payload, setPayload] = useState(null);
    const [name, setName] = useState('');

    const handleSubmit = async () => {
        try {
            await onSubmit({
                deviceType: 'webauthn',
                name: name || t('mfa.webauthn.defaultDeviceName'),
                payload: payload,
            });
            setIsOpen(false);
        } catch (e) {}
    };

    const handleSetupPasskey = async () => {
        try {
            const attestationCredential: any = await createPasskeyCredentials(data?.mfaDeviceSetupData?.options);
            setPayload(attestationCredential);
        } catch (e) {
            console.error(e);
        }
    };

    const { data } = useQuery(GET_MFA_DEVICE_SETUP_DATA, { skip: !isOpen, variables: { deviceType: 'webauthn' } });

    return (
        <>
            <Button onClick={() => setIsOpen(true)} {...props} disabled={!isWebauthnSupported || props.disabled}>
                {t('mfa.addButton')}
            </Button>

            {isOpen && (
                <Modal
                    onClose={() => setIsOpen(false)}
                    title={t('mfa.webauthn.addModalTitle')}
                    buttons={
                        <>
                            <Button onClick={() => setIsOpen(false)}>{t('mfa.cancelButton')}</Button>
                            <Button variant="primary" onClick={handleSubmit} disabled={!payload}>
                                {t('mfa.saveButton')}
                            </Button>
                        </>
                    }
                >
                    <div className="w-[28rem] max-w-full flex flex-col gap-6">
                        <p className="text-content-strong text-base">{t('mfa.webauthn.addModalDescription')}</p>

                        <div className="flex flex-col gap-2 my-2">
                            {!payload ? (
                                <Button
                                    className="mx-4"
                                    variant="primary"
                                    disabled={!data?.mfaDeviceSetupData?.options}
                                    onClick={handleSetupPasskey}
                                >
                                    {t('mfa.webauthn.addModalSetupButton')}
                                </Button>
                            ) : (
                                <p className="text-sm text-content-medium gap-2 flex items-center justify-center">
                                    <span className="text-content-success-weak text-lg flex-none">
                                        <SuccessIcon />
                                    </span>
                                    <span>{t('mfa.webauthn.addModalSetupComplete')}</span>
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-content-strong text-base font-medium">{t('mfa.nameLabel')}</label>
                            <StringField placeholder={t('mfa.namePlaceholder')} value={name} onValueChange={setName} />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

const AddAuthenticatorAppModalButton = ({
    onSubmit,
    ...props
}: React.ComponentPropsWithRef<typeof Button> & {
    onSubmit: ({ deviceType, name, payload }: { deviceType: string; name: string; payload: any }) => Promise<void>;
}) => {
    const { t } = useTranslation('users');

    const [isOpen, setIsOpen] = useState(false);

    const [verificationCode, setVerificationCode] = useState('');
    const [name, setName] = useState('');

    const handleSubmit = async () => {
        try {
            await onSubmit({
                deviceType: 'totp',
                name: name || t('mfa.totp.defaultDeviceName'),
                payload: { token: verificationCode },
            });
            setIsOpen(false);
        } catch (e) {}
    };

    const { data } = useQuery(GET_MFA_DEVICE_SETUP_DATA, { skip: !isOpen, variables: { deviceType: 'totp' } });

    const qrCodeUrl = data?.mfaDeviceSetupData.qrCodeUrl;
    const secretKey = data?.mfaDeviceSetupData.secretKey;

    return (
        <>
            <Button onClick={() => setIsOpen(true)} {...props}>
                {t('mfa.addButton')}
            </Button>

            {isOpen && (
                <Modal
                    onClose={() => setIsOpen(false)}
                    title={t('mfa.totp.addModalTitle')}
                    buttons={
                        <>
                            <Button onClick={() => setIsOpen(false)}>{t('mfa.cancelButton')}</Button>
                            <Button variant="primary" onClick={handleSubmit} disabled={verificationCode.length < 6}>
                                {t('mfa.saveButton')}
                            </Button>
                        </>
                    }
                >
                    <div className="w-[28rem] max-w-full flex flex-col gap-6">
                        <p className="text-content-strong text-base">{t('mfa.totp.addModalDescription')}</p>

                        <div className="flex gap-6 items-center justify-center p-4 my-2 bg-surface-medium rounded">
                            <img className="border-line-strong border rounded bg-surface-white" src={qrCodeUrl} />

                            <p className="text-sm text-content-medium">
                                {t('mfa.totp.addModalSecretKeyNote')}
                                <br />
                                <code className="p-0.5 px-1.5 rounded bg-surface-weak inline-block mt-2">
                                    {secretKey}
                                </code>
                            </p>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-content-strong text-base font-medium">
                                {t('mfa.totp.addModalVerificationCodeLabel')}
                            </label>
                            <IntegerField
                                className="max-w-36 tabular-nums tracking-widest"
                                placeholder="_ _ _ _ _ _"
                                maxLength={6}
                                autoComplete="one-time-code"
                                autoFocus
                                value={verificationCode}
                                onValueChange={setVerificationCode}
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-content-strong text-base font-medium">{t('mfa.nameLabel')}</label>
                            <StringField placeholder={t('mfa.namePlaceholder')} value={name} onValueChange={setName} />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

const ConfirmRegisterWithPasswordModal = ({ onConfirm, onCancel }) => {
    const { t, i18n } = useTranslation('users');
    const [password, setPassword] = useState('');

    return (
        <ConfirmModal
            title={t('mfa.confirmRegisterWithPasswordModal.title')}
            labelConfirm={t('mfa.confirmRegisterWithPasswordModal.confirmButton')}
            labelCancel={t('mfa.confirmRegisterWithPasswordModal.cancelButton')}
            onConfirm={() => onConfirm(password)} // Bulk delete
            onCancel={onCancel}
            visible={true}
        >
            <p className="mb-3">{t('mfa.confirmRegisterWithPasswordModal.message')}</p>
            <FormField label={t('mfa.confirmRegisterWithPasswordModal.passwordLabel')} required={true}>
                <StringField type="password" autoFocus value={password} onValueChange={setPassword} />
            </FormField>
        </ConfirmModal>
    );
};

const ConfirmUnregisterWithPasswordModal = ({ onConfirm, onCancel }) => {
    const { t, i18n } = useTranslation('users');
    const [password, setPassword] = useState('');

    return (
        <ConfirmModal
            title={t('mfa.confirmUnregisterWithPasswordModal.title')}
            labelConfirm={t('mfa.confirmUnregisterWithPasswordModal.confirmButton')}
            labelCancel={t('mfa.confirmUnregisterWithPasswordModal.cancelButton')}
            onConfirm={() => onConfirm(password)} // Bulk delete
            onCancel={onCancel}
            visible={true}
            danger
        >
            <p className="mb-3">{t('mfa.confirmUnregisterWithPasswordModal.message')}</p>
            <FormField label={t('mfa.confirmUnregisterWithPasswordModal.passwordLabel')} required={true}>
                <StringField type="password" autoFocus value={password} onValueChange={setPassword} />
            </FormField>
        </ConfirmModal>
    );
};

const MfaForm = ({
    className,
    hideWebauthn,
    hideTotp,
    hideStatic,
    onDevicesChange,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    hideWebauthn?: boolean;
    hideTotp?: boolean;
    hideStatic?: boolean;
    onDevicesChange?: () => Promise<void>;
}) => {
    const { t, i18n } = useTranslation('users');
    const { publishToast } = useToaster();

    const { data: mfaDevices, refetch: refetchDevices } = useQuery(GET_MFA_DEVICES);

    const passkeyDevices = mfaDevices?.mfaDevices.filter((device) => device.deviceType === 'webauthn');
    const authenticatorAppDevices = mfaDevices?.mfaDevices.filter((device) => device.deviceType === 'totp');
    const recoveryCodeDevices = mfaDevices?.mfaDevices.filter((device) => device.deviceType === 'static');

    const [registerMfaDeviceData, setRegisterMfaDeviceData] = useState(null);
    const handleSubmit = async (data) => setRegisterMfaDeviceData(data);

    const [registerMfaDevice] = useMutation(REGISTER_MFA_DEVICE);
    const handleRegisterMfaDevice = async (userPassword, { deviceType, name, payload }: any) => {
        try {
            await registerMfaDevice({ variables: { userPassword, deviceType, name, payload } });
            publishToast({ description: t('mfa.registerSuccess'), status: 'success' });
            await refetchDevices();
            if (onDevicesChange) {
                await onDevicesChange();
            }
        } catch (e) {
            console.error(e);
            publishToast({ description: t('mfa.registerError'), status: 'error' });
            throw e; // rethrow to prevent modal from closing
        }
    };

    const [unregisterMfaDeviceId, setUnregisterMfaDeviceId] = useState(null);
    const [unregisterMfaDevice] = useMutation(UNREGISTER_MFA_DEVICE);
    const handleUnregisterMfaDevice = async (userPassword, deviceId) => {
        try {
            await unregisterMfaDevice({ variables: { userPassword, deviceId } });
            publishToast({ description: t('mfa.unregisterSuccess'), status: 'success' });
            await refetchDevices();
            if (onDevicesChange) {
                await onDevicesChange();
            }
        } catch (e) {
            console.error(e);
            publishToast({ description: t('mfa.unregisterError'), status: 'error' });
        }
    };

    return (
        <div className={classnames('flex flex-col gap-6', className)} {...props}>
            {!hideWebauthn && (
                <TwoFactorMethod
                    icon={<PasskeyIcon />}
                    title={t('mfa.webauthn.title')}
                    description={t('mfa.webauthn.description')}
                    button={<AddPasskeyModalButton onSubmit={handleSubmit} />}
                >
                    {passkeyDevices?.map((device) => (
                        <TwoFactorMethodDevice
                            name={device.name}
                            description={t('mfa.twoFactorMethodDeviceDescription', {
                                createdAt: device.createdAt
                                    ? new Date(device.createdAt).toLocaleString(i18n.language)
                                    : t('mfa.unknownUsed'),
                                lastUsedAt: device.lastUsedAt
                                    ? new Date(device.lastUsedAt).toLocaleString(i18n.language)
                                    : t('mfa.neverUsed'),
                            })}
                            button={
                                <Button
                                    className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                                    onClick={() => setUnregisterMfaDeviceId(device.deviceId)}
                                >
                                    <DeleteIcon />
                                </Button>
                            }
                        />
                    ))}
                </TwoFactorMethod>
            )}

            {!hideTotp && (
                <TwoFactorMethod
                    icon={<AuthenticatorAppIcon />}
                    title={t('mfa.totp.title')}
                    description={t('mfa.totp.description')}
                    button={<AddAuthenticatorAppModalButton onSubmit={handleSubmit} />}
                >
                    {authenticatorAppDevices?.map((device) => (
                        <TwoFactorMethodDevice
                            name={device.name}
                            description={t('mfa.twoFactorMethodDeviceDescription', {
                                createdAt: device.createdAt
                                    ? new Date(device.createdAt).toLocaleString(i18n.language)
                                    : t('mfa.unknownUsed'),
                                lastUsedAt: device.lastUsedAt
                                    ? new Date(device.lastUsedAt).toLocaleString(i18n.language)
                                    : t('mfa.neverUsed'),
                            })}
                            button={
                                <Button
                                    className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                                    onClick={() => setUnregisterMfaDeviceId(device.deviceId)}
                                >
                                    <DeleteIcon />
                                </Button>
                            }
                        />
                    ))}
                </TwoFactorMethod>
            )}

            {!hideStatic && (
                <TwoFactorMethod
                    icon={<RecoveryCodeIcon />}
                    title={t('mfa.static.title')}
                    description={t('mfa.static.description')}
                    button={<AddRecoveryCodesModalButton onSubmit={handleSubmit} />}
                >
                    {recoveryCodeDevices?.map((device) => (
                        <TwoFactorMethodDevice
                            name={device.name}
                            description={t('mfa.twoFactorMethodDeviceDescription', {
                                createdAt: device.createdAt
                                    ? new Date(device.createdAt).toLocaleString(i18n.language)
                                    : t('mfa.unknownUsed'),
                                lastUsedAt: device.lastUsedAt
                                    ? new Date(device.lastUsedAt).toLocaleString(i18n.language)
                                    : t('mfa.neverUsed'),
                            })}
                            button={
                                <div className="flex gap-2 items-center">
                                    <Button
                                        className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                                        onClick={() => setUnregisterMfaDeviceId(device.deviceId)}
                                    >
                                        <DeleteIcon />
                                    </Button>
                                </div>
                            }
                        />
                    ))}
                </TwoFactorMethod>
            )}

            {!!registerMfaDeviceData && (
                <ConfirmRegisterWithPasswordModal
                    onConfirm={async (password) => {
                        await handleRegisterMfaDevice(password, registerMfaDeviceData);
                        setRegisterMfaDeviceData(null);
                    }}
                    onCancel={() => setRegisterMfaDeviceData(null)}
                />
            )}

            {!!unregisterMfaDeviceId && (
                <ConfirmUnregisterWithPasswordModal
                    onConfirm={async (password) => {
                        await handleUnregisterMfaDevice(password, unregisterMfaDeviceId);
                        setUnregisterMfaDeviceId(null);
                    }}
                    onCancel={() => setUnregisterMfaDeviceId(null)}
                />
            )}
        </div>
    );
};

export default MfaForm;
